import { Playbook, Phase } from '../types';
import { 
  throttledStorageSave, 
  flushPendingSaves, 
  safeStorageGet, 
  safeStorageRemove 
} from './storageUtils';

/**
 * Keys for localStorage
 */
const PROGRESS_KEY_PREFIX = 'playbook_progress_';

/**
 * Interface for the progress data stored in localStorage
 */
export interface PlaybookProgress {
  activePhaseId: string;
  completedTasks: Record<string, boolean>;
  lastUpdated?: number; // Timestamp for tracking stale data
}

/**
 * Save progress for a specific playbook
 */
export const savePlaybookProgress = (
  playbookId: string, 
  activePhaseId: string, 
  completedTasks: Record<string, boolean>
): boolean => {
  if (!playbookId || !activePhaseId) {
    console.warn('Cannot save progress: missing playbook or phase ID');
    return false;
  }
  
  try {
    const progressData: PlaybookProgress = {
      activePhaseId,
      completedTasks,
      lastUpdated: Date.now()
    };
    
    // Use throttled storage to prevent excessive writes
    throttledStorageSave(`${PROGRESS_KEY_PREFIX}${playbookId}`, progressData);
    return true;
  } catch (error) {
    console.error('Failed to save playbook progress:', error);
    return false;
  }
};

/**
 * Flush any pending progress saves - useful when component unmounts
 * @param playbookId Optional specific playbook to flush
 */
export const flushProgressSaves = (playbookId?: string): void => {
  try {
    if (playbookId) {
      flushPendingSaves(`${PROGRESS_KEY_PREFIX}${playbookId}`);
    } else {
      flushPendingSaves();
    }
  } catch (error) {
    console.error('Error flushing progress saves:', error);
  }
};

/**
 * Load progress for a specific playbook
 */
export const loadPlaybookProgress = (playbookId: string): PlaybookProgress | null => {
  if (!playbookId) {
    return null;
  }
  
  try {
    const defaultProgress = null;
    return safeStorageGet<PlaybookProgress | null>(`${PROGRESS_KEY_PREFIX}${playbookId}`, defaultProgress);
  } catch (error) {
    console.error('Failed to load playbook progress:', error);
    return null;
  }
};

/**
 * Calculate the progress percentage for a phase
 */
export const calculatePhaseProgress = (
  phase: Phase, 
  completedTasks: Record<string, boolean>
): number => {
  // Guard against invalid data
  if (!phase || !Array.isArray(phase.tasks)) {
    return 0;
  }
  
  // Filter out Pro Tips
  const actualTasks = phase.tasks.filter(task => task.title !== 'Pro Tip');
  const taskCount = actualTasks.length;
  if (taskCount === 0) return 0;
  
  let completedCount = 0;
  
  // Count completed tasks, handling potential bad data
  for (const task of actualTasks) {
    if (task && task.id && completedTasks[task.id] === true) {
      completedCount++;
    }
  }
  
  return Math.round((completedCount / taskCount) * 100);
};

/**
 * Calculate the overall progress percentage for a playbook
 */
export const calculateOverallProgress = (
  playbook: Playbook, 
  completedTasks: Record<string, boolean>
): number => {
  // Guard against invalid data
  if (!playbook || !Array.isArray(playbook.phases)) {
    return 0;
  }
  
  let totalTaskCount = 0;
  let completedTaskCount = 0;
  
  // Calculate total tasks and completed tasks
  for (const phase of playbook.phases) {
    if (phase && Array.isArray(phase.tasks)) {
      // Filter out Pro Tips
      const actualTasks = phase.tasks.filter(task => task.title !== 'Pro Tip');
      totalTaskCount += actualTasks.length;
      
      for (const task of actualTasks) {
        if (task && task.id && completedTasks[task.id] === true) {
          completedTaskCount++;
        }
      }
    }
  }
  
  if (totalTaskCount === 0) return 0;
  
  return Math.round((completedTaskCount / totalTaskCount) * 100);
};

/**
 * Get the count of completed tasks
 */
export const getCompletedTaskCount = (completedTasks: Record<string, boolean>): number => {
  if (!completedTasks || typeof completedTasks !== 'object') {
    return 0;
  }
  
  return Object.values(completedTasks).filter(value => value === true).length;
};

/**
 * Reset progress for a specific playbook
 */
export const resetPlaybookProgress = (playbookId: string): boolean => {
  if (!playbookId) {
    return false;
  }
  
  try {
    return safeStorageRemove(`${PROGRESS_KEY_PREFIX}${playbookId}`);
  } catch (error) {
    console.error('Failed to reset playbook progress:', error);
    return false;
  }
};

/**
 * Clear all playbook progress (useful for logout or account reset)
 */
export const clearAllPlaybookProgress = (): void => {
  try {
    const keysToRemove: string[] = [];
    
    // Find all playbook progress keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PROGRESS_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    // Remove each key
    keysToRemove.forEach(key => {
      safeStorageRemove(key);
    });
  } catch (error) {
    console.error('Failed to clear all playbook progress:', error);
  }
}; 